import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, Type, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideNativeDateAdapter } from '@angular/material/core';
import { debounceTime, Observable, Subject } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PageInfo } from '../../services/api.service';
import { YshDotsComponent } from "../ysh-dots/ysh-dots.component";
import { YshButtonTheme, YshButtonComponent } from '../ysh-button/ysh-button.component';

export interface ColumnDetail<T> {
  /**
   * Column id + attrubute of entity to display. If empty, entity will be passed
   */
  field?: keyof T & string,

  /**
   * Field with icon (name from Material Icons)
   */
  fieldIcon?: string,
  
  /**
   * Function when tapping field
   */
  fieldTap?: (item: T) => void,

  /**
   * Column header title
   */
  title?: string,

  /**
   * Column supports sorting
   */
  sortable?: boolean,

  /**
   * Sorting paramater to use for API Call
   */
  sortParam?: string,

  /**
   * Cell size for smaller/larger data
   */
  cellSize?: 'small' | 'large',

  /**
   * Optional component to use for display instead of plain value
   */

  component?: Type<Component & WithValue<T>> ,

  /**
   * Optional factory to provide a component along with properties
   */
  componentWithInputs?: (item: T) => ComponentWithInputs<unknown>
}

export type WithValue<T> = { value: T } | { value : string};

export class ComponentWithInputs<T> {
  constructor(public component: Type<T>, public inputs: Partial<T>) {} 
}

export interface TableState {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  filter?: string;
  dateStartInclusive?: string;
  dateEndInclusive?: string;
  /** Values set by select filters, keyed by the filter's param key. */
  [key: string]: any;
}

export enum TableFilter {
  Search = 'search',
  StartEndDate = 'startEndDate',
  Select = 'select'
}

export interface SelectFilterOption {
  label: string;
  value: any;
}

export interface SearchFilter {
  type: TableFilter.Search;
}

export interface DateRangeFilter {
  type: TableFilter.StartEndDate;
}

export interface SelectFilter {
  type: TableFilter.Select;
  /** Query param key — also the TableState key the selected value is written to. */
  key: string;
  label: string;
  /** Label shown for the empty/"no filter" option. Defaults to "All". */
  allLabel?: string;
  options: SelectFilterOption[];
}

/** A single entry in the data source's `filters` array, discriminated by `type`. */
export type TableFilterConfig = SearchFilter | DateRangeFilter | SelectFilter;

export interface TableAction {
  buttonLabel: string;
  buttonIcon?: string;
  buttonTheme?: YshButtonTheme;
  onTap: () => void;
}

export interface TableDataSouce<T> {
  title: string;
  columns: ColumnDetail<T>[];
  displayRowNav?: boolean;
  /** When set, the row nav link is only shown for items returning true. */
  canNavigateRow?: (item: T) => boolean;
  filters: TableFilterConfig[];
  actions: TableAction[];
  noDataMessage?: string;
  /** Initial sort applied on load and reflected in the column header. The
   * user can still flip/clear it by tapping the header. `field` must match a
   * column's `field` (or `title` when it has no field). */
  defaultSort?: { field: string; order: 'asc' | 'desc' };
  load?: (state: TableState) => Observable<{ items: T[], paginationInfo?: PageInfo, }>;
}

@Component({
    selector: 'ysh-table-set',
    templateUrl: './ysh-table-set.component.html',
    styleUrl: './ysh-table-set.component.scss',
    providers: [provideNativeDateAdapter()],
    hostDirectives: [MatSort],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatPaginatorModule,
        MatTableModule,
        MatSortModule,
        MatDatepickerModule,
        MatTooltipModule,
        FormsModule,
        YshDotsComponent,
        YshButtonComponent,
        CommonModule
    ]
})
export class YshTableSetComponent<T> implements AfterViewInit {

  readonly DEBOUNCE_TIME = 500;

  _dataSource: TableDataSouce<T>
  _loading = true;
  _fields: string[] = [];
  _items: T[] = [];
  _paginationInfo: PageInfo;
  _hasSearchFilter = false;
  _hasStartEndDateFilter = false;
  _selectFilters: SelectFilter[] = [];
  _hasHeader = false;

  @Input() set dataSource(dataSource: TableDataSouce<any>) {
    this._dataSource = dataSource;
    if (dataSource){
      const fields = dataSource.columns.map(item => item.field || item.title);
      const navField =  dataSource.displayRowNav ? ["navigation"] : [];
      this._fields = [...fields, ...navField];
      const filters = dataSource.filters || [];
      this._hasSearchFilter = filters.some(filter => filter.type === TableFilter.Search);
      this._hasStartEndDateFilter = filters.some(filter => filter.type === TableFilter.StartEndDate);
      this._selectFilters = filters.filter((filter): filter is SelectFilter => filter.type === TableFilter.Select);
      this._hasHeader = [filters, dataSource.actions, dataSource.title].some(a => a.length);
      if (dataSource.defaultSort) {
        const column = dataSource.columns.find(col => (col.field || col.title) === dataSource.defaultSort.field);
        this.tableState.sortBy = column?.sortParam || dataSource.defaultSort.field;
        this.tableState.sortOrder = dataSource.defaultSort.order;
      }
      this.loadData(this.tableState);
    }
  }

  @Output() onItemTap: EventEmitter<T> = new EventEmitter();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private searchSubject = new Subject<string>;
  private tableState: TableState = { page: 1 };
  selectFilterValues: Record<string, any> = {};
  filter: string;
  filtering: boolean;

  constructor(private cdRef: ChangeDetectorRef){}

  ngAfterViewInit() {
    if (this._dataSource?.defaultSort && this.sort) {
      // Reflect the default in the header arrow and keep flipping limited to
      // asc <-> desc (no "clear" step) so the column stays sorted.
      this.sort.disableClear = true;
      this.sort.active = this._dataSource.defaultSort.field;
      this.sort.direction = this._dataSource.defaultSort.order;
      this.cdRef.detectChanges();
    }
    this.searchSubject.
      pipe(debounceTime(this.DEBOUNCE_TIME)).
      subscribe(value => {
        this.filtering = false;
        this.tableState.filter = value;
        this.tableState.page = 1;
        this.paginator.pageIndex = 0;
        this.loadData(this.tableState);
      });
  }

  didChangeFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filtering = true;
    this.filter = filterValue;
    this.searchSubject.next(filterValue?.length ? filterValue : undefined);
  }

  didChangeSelectFilter(key: string, value: any) {
    this.tableState[key] = value;
    this.tableState.page = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadData(this.tableState);
  }

  didChangeDate(startDate: string, endDate: string){
    this.tableState.dateStartInclusive = startDate;
    this.tableState.dateEndInclusive = endDate;
    this.loadData(this.tableState);
  }

  didChangePage(page: PageEvent) {
    this.tableState.page = page.pageIndex + 1;
    this.loadData(this.tableState);
  }

  didChangeSort(sort: Sort) {
    const column = this._dataSource?.columns.find(col => (col.field || col.title) === sort.active);
    const sortBy = column?.sortParam || sort.active;
    this.tableState.sortBy = sort.direction ? sortBy : undefined;
    this.tableState.sortOrder = sort.direction || undefined;
    this.loadData(this.tableState);
  }

  didTapItem(item: T){
    this.onItemTap.emit(item);
  }

  private loadData(state: TableState) {
    this._dataSource.load(state)?.subscribe((data)=>{
      this._items = data.items;
      this._paginationInfo = data.paginationInfo;
      this._loading = false;
    })
  }
}
